import React from "react";
import "ag-grid-enterprise";
import {
  Badge,
  Alert,
  Box,
  Code,
  Divider,
  Group,
  Modal,
  Paper,
  ScrollArea,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  TextInput,
  Text,
  Title,
} from "@mantine/core";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import {
  DROFUS_INTERFACE_OCCURRENCE_FIELD_GROUPS,
  DROFUS_INTERFACE_OCCURRENCE_FIELD_META,
  DROFUS_INTERFACE_OCCURRENCE_NEED_RULES,
} from "@multiconsult-group/multiplacer-core";
import type { PostMultiplacerDrofusInterfaceOccurrencesApiResponse } from "../../openapi";
import { DrofusOccurrenceRoomViewer } from "./DrofusOccurrenceRoomViewer";

type DrofusForekomstRow = PostMultiplacerDrofusInterfaceOccurrencesApiResponse["items"][number];

type DrofusSourceFieldRow = {
  group: string;
  groupLabel: string;
  fieldId: string;
  displayName: string;
  normalizedPath: string | null;
  value: unknown;
  formattedValue: string;
  active: boolean;
  sourceAvailable: boolean;
};

type DrofusForekomstDetailsModalProps = {
  row: DrofusForekomstRow | null;
  opened: boolean;
  onClose: () => void;
};

const disciplineColorById: Record<string, string> = {
  RIE: "#f59f00",
  RIKT: "#1971c2",
  RIAut: "#9c36b5",
  RIV: "#0c8599",
};

const groupLabelById: Record<string, string> = {
  occurrence: "Forekomst",
  room: "Rom",
  article: "Artikkel",
  system: "System",
  tfmStatus: "TFM/status",
  occurrenceData: "Forekomstdata",
  articleNeeds: "Artikkelbehov",
};

const allFieldsDefaultColDef: ColDef<DrofusSourceFieldRow> = {
  sortable: true,
  filter: true,
  resizable: true,
  minWidth: 120,
  enableRowGroup: false,
  enablePivot: false,
  enableValue: false,
};

function formatValue(value: unknown): string {
  if (value == null || value === "") return "-";
  if (typeof value === "boolean") return value ? "Ja" : "Nei";
  if (Array.isArray(value)) return value.length > 0 ? value.join(", ") : "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function getByPath(source: unknown, path: string): unknown {
  return path.split(".").reduce<unknown>((current, key) => {
    if (current == null || typeof current !== "object") return undefined;
    return (current as Record<string, unknown>)[key];
  }, source);
}

function isTruthyNeedValue(value: unknown): boolean {
  if (value === true) return true;
  if (typeof value === "number") return value > 0;
  if (typeof value === "string") return value.trim().length > 0;
  return false;
}

const DetailField: React.FC<{ label: string; value: unknown; mono?: boolean }> = ({
  label,
  value,
  mono,
}) => (
  <Box>
    <Text size="xs" c="dimmed">
      {label}
    </Text>
    {mono ? (
      <Code>{formatValue(value)}</Code>
    ) : (
      <Text size="sm" fw={500}>
        {formatValue(value)}
      </Text>
    )}
  </Box>
);

const DetailSection: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <Paper withBorder radius="sm" p="md">
    <Stack gap="sm">
      <Title order={5}>{title}</Title>
      {children}
    </Stack>
  </Paper>
);

const DetailSubSection: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <Stack gap="xs">
    <Text size="xs" fw={700} c="dimmed">
      {title}
    </Text>
    {children}
  </Stack>
);

const BooleanNeed: React.FC<{ label: string; value: boolean }> = ({ label, value }) => (
  <Group justify="space-between" wrap="nowrap">
    <Text size="sm">{label}</Text>
    <Badge
      radius="sm"
      styles={{
        root: {
          backgroundColor: value ? "#d3f9d8" : "#f1f3f5",
          border: `1px solid ${value ? "#2f9e44" : "#ced4da"}`,
          color: value ? "#2b8a3e" : "#495057",
        },
        label: {
          color: "inherit",
        },
      }}
    >
      {value ? "Ja" : "Nei"}
    </Badge>
  </Group>
);

const DisciplineBadge: React.FC<{ discipline: string }> = ({ discipline }) => {
  const color = disciplineColorById[discipline] ?? "#868e96";

  return (
    <Badge
      size="sm"
      radius="sm"
      styles={{
        root: {
          backgroundColor: "#ffffff",
          border: `1px solid ${color}`,
          color,
        },
        label: {
          color: "inherit",
        },
      }}
    >
      {discipline}
    </Badge>
  );
};

const SourceFieldValueRenderer: React.FC<ICellRendererParams<DrofusSourceFieldRow, unknown>> = ({
  data,
}) => {
  if (!data) return null;

  return (
    <Group gap="xs" wrap="nowrap">
      <Text size="sm" fw={data.active ? 700 : 500}>
        {data.formattedValue}
      </Text>
    </Group>
  );
};

const SourceFieldDisplayNameRenderer: React.FC<
  ICellRendererParams<DrofusSourceFieldRow, string>
> = ({ data, value }) => (
  <Text size="sm" title={data?.fieldId}>
    {value}
  </Text>
);

const SourceFieldActiveRenderer: React.FC<ICellRendererParams<DrofusSourceFieldRow, boolean>> = ({
  value,
}) => (
  <Badge
    size="sm"
    radius="sm"
    styles={{
      root: {
        backgroundColor: value ? "#d3f9d8" : "#f1f3f5",
        border: `1px solid ${value ? "#2f9e44" : "#ced4da"}`,
        color: value ? "#2b8a3e" : "#495057",
      },
      label: { color: "inherit" },
    }}
  >
    {value ? "Ja" : "Nei"}
  </Badge>
);

const SourceFieldCodeRenderer: React.FC<ICellRendererParams<DrofusSourceFieldRow, string | null>> = ({
  value,
}) => {
  if (!value) return <Text size="sm" c="dimmed">-</Text>;
  return <Code>{value}</Code>;
};

const SourceFieldsTable: React.FC<{
  row: DrofusForekomstRow;
  groups: readonly string[];
}> = ({ row, groups }) => {
  const sourceValues = row.sourceValues as Record<string, unknown> | undefined;
  const [quickFilter, setQuickFilter] = React.useState("");

  if (!sourceValues) {
    return (
      <Alert color="yellow" variant="light" radius="sm">
        Denne kjøringen mangler lagrede kildeverdier. Kjør en ny dRofus-sync for å se rådata fra
        API-et her.
      </Alert>
    );
  }

  const rowData = DROFUS_INTERFACE_OCCURRENCE_FIELD_META.filter((field) =>
    groups.includes(field.group)
  ).map((field) => {
    const value = sourceValues[field.id];
    const active = isTruthyNeedValue(value);

    return {
      group: field.group,
      groupLabel: groupLabelById[field.group] ?? field.group,
      fieldId: field.id,
      displayName: field.displayName,
      normalizedPath: field.normalizedPath ?? null,
      value,
      formattedValue: formatValue(value),
      active,
      sourceAvailable: true,
    };
  });

  const columnDefs: ColDef<DrofusSourceFieldRow>[] = [
    {
      headerName: "Gruppe",
      field: "groupLabel",
      width: 150,
      pinned: "left",
    },
    {
      headerName: "Displaynavn",
      field: "displayName",
      minWidth: 420,
      flex: 1,
      wrapText: true,
      autoHeight: true,
      tooltipField: "fieldId",
      cellRenderer: SourceFieldDisplayNameRenderer,
    },
    {
      headerName: "Verdi",
      field: "formattedValue",
      width: 220,
      cellRenderer: SourceFieldValueRenderer,
    },
    {
      headerName: "Aktiv",
      field: "active",
      width: 110,
      sortable: true,
      filter: true,
      cellRenderer: SourceFieldActiveRenderer,
    },
  ];

  return (
    <Stack gap="sm">
      <TextInput
        value={quickFilter}
        onChange={(event) => setQuickFilter(event.currentTarget.value)}
        placeholder="Søk i gruppe, displaynavn eller verdi"
        size="sm"
        radius="sm"
        styles={{
          input: {
            maxWidth: 640,
          },
        }}
      />
      <Box className="ag-theme-quartz" h="42vh" w="100%">
        <AgGridReact<DrofusSourceFieldRow>
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={allFieldsDefaultColDef}
          quickFilterText={quickFilter}
          rowHeight={34}
          headerHeight={36}
          suppressCellFocus
          suppressRowClickSelection
          animateRows={false}
        />
      </Box>
    </Stack>
  );
};

const AllFieldsTable: React.FC<{ row: DrofusForekomstRow }> = ({ row }) => {
  const sourceValues = row.sourceValues as Record<string, unknown> | undefined;
  const [quickFilter, setQuickFilter] = React.useState("");
  const rowData = React.useMemo<DrofusSourceFieldRow[]>(
    () =>
      DROFUS_INTERFACE_OCCURRENCE_FIELD_GROUPS.flatMap((group) =>
        DROFUS_INTERFACE_OCCURRENCE_FIELD_META.filter((field) => field.group === group).map((field) => {
          const value =
            sourceValues && field.id in sourceValues
              ? sourceValues[field.id]
              : field.normalizedPath
                ? getByPath(row.normalized, field.normalizedPath)
                : undefined;

          return {
            group,
            groupLabel: groupLabelById[group] ?? group,
            fieldId: field.id,
            displayName: field.displayName,
            normalizedPath: field.normalizedPath ?? null,
            value,
            formattedValue: formatValue(value),
            active: isTruthyNeedValue(value),
            sourceAvailable: Boolean(sourceValues),
          };
        })
      ),
    [row.normalized, sourceValues]
  );

  const columnDefs = React.useMemo<ColDef<DrofusSourceFieldRow>[]>(
    () => [
      {
        headerName: "Gruppe",
        field: "groupLabel",
        width: 150,
        pinned: "left",
      },
      {
        headerName: "API-felt",
        field: "fieldId",
        width: 300,
        pinned: "left",
        cellRenderer: SourceFieldCodeRenderer,
      },
      {
        headerName: "Displaynavn",
        field: "displayName",
        minWidth: 420,
        flex: 1,
        wrapText: true,
        autoHeight: true,
      },
      {
        headerName: "Normalisert",
        field: "normalizedPath",
        width: 260,
        cellRenderer: SourceFieldCodeRenderer,
      },
      {
        headerName: "Verdi",
        field: "formattedValue",
        width: 230,
        cellRenderer: SourceFieldValueRenderer,
      },
    ],
    []
  );

  return (
    <Stack gap="md">
      {!sourceValues && (
        <Alert color="yellow" variant="light" radius="sm">
          Denne kjøringen mangler lagrede kildeverdier. Kjør en ny dRofus-sync for å se alle
          API-feltene og nøyaktig behovsgrunnlag.
        </Alert>
      )}
      <TextInput
        value={quickFilter}
        onChange={(event) => setQuickFilter(event.currentTarget.value)}
        placeholder="Søk i gruppe, API-felt, displaynavn, normalisert path eller verdi"
        size="sm"
        radius="sm"
        styles={{
          input: {
            maxWidth: 640,
          },
        }}
      />
      <Box className="ag-theme-quartz" h="62vh" w="100%">
        <AgGridReact<DrofusSourceFieldRow>
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={allFieldsDefaultColDef}
          quickFilterText={quickFilter}
          rowHeight={34}
          headerHeight={36}
          suppressCellFocus
          suppressRowClickSelection
          animateRows={false}
        />
      </Box>
    </Stack>
  );
};

const NeedSourceFields: React.FC<{ row: DrofusForekomstRow }> = ({ row }) => {
  const sourceValues = row.sourceValues as Record<string, unknown> | undefined;

  if (!sourceValues) {
    return (
      <Alert color="yellow" variant="light" radius="sm">
        Kildeverdier for behovsreglene finnes ikke i dette snapshotet. Kjør ny sync for å se hvilke
        dRofus-felt som utløste hvert behov.
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      {Object.entries(DROFUS_INTERFACE_OCCURRENCE_NEED_RULES).map(([needKey, rule]) => {
        const sourceRows = rule.sourceFieldIds.map((fieldId) => {
          const field = DROFUS_INTERFACE_OCCURRENCE_FIELD_META.find((meta) => meta.id === fieldId);
          const value = sourceValues[fieldId];
          return {
            fieldId,
            displayName: field?.displayName ?? fieldId,
            value,
            active: isTruthyNeedValue(value),
          };
        });
        const activeRows = sourceRows.filter((item) => item.active);

        return (
          <DetailSubSection key={needKey} title={`${rule.label} (${rule.affectedDiscipline})`}>
            {activeRows.length === 0 ? (
              <Text size="sm" c="dimmed">
                Ingen kildefelt var aktive.
              </Text>
            ) : (
              <Table withTableBorder withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 290 }}>API-felt</Table.Th>
                    <Table.Th>Displaynavn</Table.Th>
                    <Table.Th style={{ width: 180 }}>Verdi</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {activeRows.map((item) => (
                    <Table.Tr key={item.fieldId}>
                      <Table.Td>
                        <Code>{item.fieldId}</Code>
                      </Table.Td>
                      <Table.Td>{item.displayName}</Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={700}>
                          {formatValue(item.value)}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </DetailSubSection>
        );
      })}
    </Stack>
  );
};

export const DrofusForekomstDetailsModal: React.FC<DrofusForekomstDetailsModalProps> = ({
  row,
  opened,
  onClose,
}) => {
  const [activeSection, setActiveSection] = React.useState("occurrence");

  if (!row) {
    return (
      <Modal opened={opened} onClose={onClose} title="Forekomst" centered>
        <Text size="sm" c="dimmed">
          Ingen forekomst valgt.
        </Text>
      </Modal>
    );
  }

  const { normalized } = row;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Forekomst ${row.occurrenceId}`}
      size="80vw"
      centered
      styles={{
        content: { height: "88vh" },
        body: { paddingTop: 0 },
      }}
    >
      <ScrollArea.Autosize mah="calc(88vh - 84px)" type="auto" offsetScrollbars>
        <Stack gap="md" pb="md">
          <Paper withBorder radius="sm" p="md">
            <Group justify="space-between" align="flex-start">
              <Box>
                <Title order={4}>{normalized.article.compCode ?? "Ukjent compCode"}</Title>
                <Text size="sm" c="dimmed">
                  {normalized.article.name ?? "Ukjent artikkel"}
                </Text>
                <Text size="sm" c="dimmed">
                  {normalized.room.functionNo ?? "Ukjent rom"} ·{" "}
                  {normalized.room.fullName ?? "Ukjent romnavn"}
                </Text>
              </Box>
              <Group gap="xs">
                <Badge color={row.interfaceRelevant ? "teal" : "gray"} radius="sm">
                  {row.interfaceRelevant ? "Interface-relevant" : "Ikke relevant"}
                </Badge>
                {row.affectedDisciplines.map((discipline) => (
                  <DisciplineBadge key={discipline} discipline={discipline} />
                ))}
              </Group>
            </Group>
          </Paper>

          <SegmentedControl
            size="sm"
            color="blue"
            value={activeSection}
            onChange={setActiveSection}
            data={[
              { value: "occurrence", label: "Forekomst" },
              { value: "room", label: "Rom" },
              { value: "article", label: "Artikkel" },
              { value: "system", label: "System" },
              { value: "needs", label: "Behov" },
              { value: "fields", label: "Alle felt" },
              { value: "metadata", label: "Metadata" },
            ]}
            styles={{
              root: {
                alignSelf: "flex-start",
                border: "1px solid var(--mantine-color-blue-3)",
                backgroundColor: "var(--mantine-color-blue-0)",
              },
              label: {
                fontWeight: 700,
              },
            }}
          />

          {activeSection === "occurrence" && (
            <DetailSection title="Forekomst">
              <DetailSubSection title="Basis">
                <SimpleGrid cols={2} spacing="sm">
                  <DetailField label="ID" value={normalized.occurrence.id} mono />
                  <DetailField label="Stable key" value={row.stableKey} mono />
                  <DetailField label="Antall" value={normalized.occurrence.quantity} />
                  <DetailField label="Eksisterende antall" value={normalized.occurrence.existingQuantity} />
                  <DetailField label="Netto antall" value={normalized.occurrence.netQuantity} />
                  <DetailField label="Løpenummer" value={normalized.occurrence.runNo} mono />
                  <DetailField label="RefString" value={normalized.occurrence.refString} mono />
                  <DetailField label="Utvidet TFM-ID" value={normalized.occurrence.extendedTfmId} mono />
                  <DetailField label="RefCompOcc" value={normalized.occurrence.refCompOcc} mono />
                  <DetailField label="Spesiell funksjonell rolle" value={normalized.occurrence.specialFunctionalRole} />
                  <DetailField label="Ansvar" value={normalized.occurrence.responsibility} />
                  <DetailField label="Overstyrt ansvar" value={normalized.occurrence.overriddenResponsibility} />
                  <DetailField label="Status" value={normalized.occurrence.statusText} />
                  <DetailField label="Prioritet" value={normalized.occurrence.priority} />
                  <DetailField label="MMI" value={normalized.occurrence.mmiIds} />
                  <DetailField label="Programert status" value={normalized.occurrence.programmedStatusIds} />
                </SimpleGrid>
              </DetailSubSection>

              <Divider />

              <DetailSubSection title="Forekomstdata">
                <SimpleGrid cols={2} spacing="sm">
                  <DetailField label="ControlSystemType" value={normalized.occurrenceData.controlSystemType} />
                  <DetailField label="ControlledBy" value={normalized.occurrenceData.controlledBy} />
                  <DetailField label="Global ID" value={normalized.occurrenceData.globalId} mono />
                  <DetailField label="Modellnavn" value={normalized.occurrenceData.modelName} />
                  <DetailField label="RefLocRoomServe" value={normalized.occurrenceData.refLocRoomServe} mono />
                  <DetailField label="Spenning" value={normalized.occurrenceData.statedVoltage} />
                  <DetailField label="Frekvens" value={normalized.occurrenceData.statedFrequency} />
                  <DetailField label="Effekt W" value={normalized.occurrenceData.statedPowerW} />
                  <DetailField label="Ampere A" value={normalized.occurrenceData.statedAmpere} />
                  <DetailField label="Feltbus" value={normalized.occurrenceData.fieldbus} />
                  <DetailField label="Nettverksnavn" value={normalized.occurrenceData.networkName} />
                  <DetailField label="MAC" value={normalized.occurrenceData.macAddress} mono />
                  <DetailField label="IP" value={normalized.occurrenceData.ipAddress} mono />
                </SimpleGrid>
              </DetailSubSection>
            </DetailSection>
          )}

          {activeSection === "room" && (
            <DetailSection title="Rom">
              <SimpleGrid cols={2} spacing="sm">
                <DetailField label="Rom ID" value={normalized.room.id} mono />
                <DetailField label="Funksjonsnummer" value={normalized.room.functionNo} mono />
                <DetailField label="Arkitektnummer" value={normalized.room.architectNo} mono />
                <DetailField label="Fullt navn" value={normalized.room.fullName} />
                <DetailField label="Klassifisering 103" value={normalized.room.classification103Code} mono />
                <DetailField label="Klassifisering 109" value={normalized.room.classification109Code} mono />
              </SimpleGrid>
              <Divider />
              <DrofusOccurrenceRoomViewer
                projectNo={Number(row.projectNo)}
                roomFunctionNo={normalized.room.functionNo}
                articleCompCode={normalized.article.compCode}
                articleToBeDrawn={normalized.article.toBeDrawn}
              />
            </DetailSection>
          )}

          {activeSection === "article" && (
            <DetailSection title="Artikkel">
              <SimpleGrid cols={2} spacing="sm">
                <DetailField label="Artikkel ID" value={normalized.article.id} mono />
                <DetailField label="CompCode" value={normalized.article.compCode} mono />
                <DetailField label="Navn" value={normalized.article.name} />
                <DetailField label="Ansvar" value={normalized.article.responsibility} />
                <DetailField label="Skal modelleres" value={normalized.article.toBeDrawn} />
                <DetailField label="BIP" value={normalized.article.bip} />
              </SimpleGrid>
              <Divider />
              <DetailSubSection title="Kildeverdier fra dRofus">
                <SourceFieldsTable row={row} groups={["article", "articleNeeds"]} />
              </DetailSubSection>
            </DetailSection>
          )}

          {activeSection === "system" && (
            <DetailSection title="System">
              <SimpleGrid cols={2} spacing="sm">
                <DetailField label="Primærsystem ID" value={normalized.system.primarySystemId} mono />
                <DetailField label="Systemkomponent" value={normalized.system.isSystemComponent} />
                <DetailField label="Komponent" value={normalized.system.isComponent} />
                <DetailField label="Har systemer" value={normalized.system.hasSystems} />
                <DetailField label="Er medlem av systemene" value={normalized.system.systemMembership} />
              </SimpleGrid>
            </DetailSection>
          )}

          {activeSection === "needs" && (
            <DetailSection title="Behov og fag">
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                <Stack gap="xs">
                  <BooleanNeed label="Elektro samlet" value={normalized.needs.electrical} />
                  <BooleanNeed label="IKT samlet" value={normalized.needs.ikt} />
                  <BooleanNeed label="Automasjon samlet" value={normalized.needs.automation} />
                  <BooleanNeed label="VVS samlet" value={normalized.needs.vvs} />
                  <Divider />
                  <DetailField label="Effekt normal drift (kW)" value={normalized.needs.normalPowerKw} />
                  <DetailField label="Effekt standby (kW)" value={normalized.needs.standbyPowerKw} />
                  <DetailField label="Nettverksuttak" value={normalized.needs.networkOutletCount} />
                </Stack>
                <Stack gap="xs">
                  <BooleanNeed label="SD-tilkobling" value={normalized.needs.sdConnection === true} />
                  <BooleanNeed label="Romkontroll" value={normalized.needs.roomControl === true} />
                  <BooleanNeed label="Intern automatikk" value={normalized.needs.internalAutomation === true} />
                  <BooleanNeed label="BUS-kommunikasjon" value={normalized.needs.busCommunication === true} />
                  <BooleanNeed label="Vann/væsker" value={normalized.needs.vvsWater} />
                  <BooleanNeed label="Avløp" value={normalized.needs.vvsDrainage} />
                  <BooleanNeed label="Ventilasjon/utløp" value={normalized.needs.vvsVentilation} />
                  <BooleanNeed label="Kjøling" value={normalized.needs.vvsCooling} />
                  <BooleanNeed label="Varmeavgivelse" value={normalized.needs.vvsHeatEmission} />
                  <BooleanNeed label="Temperatur/luftfuktighet" value={normalized.needs.vvsTemperatureHumidity} />
                  <BooleanNeed label="Medisinsk/teknisk gass" value={normalized.needs.medicalGas} />
                </Stack>
              </SimpleGrid>
              <Divider />
              <DetailSubSection title="Behovsgrunnlag">
                <NeedSourceFields row={row} />
              </DetailSubSection>
            </DetailSection>
          )}

          {activeSection === "fields" && (
            <DetailSection title="Alle dRofus-felt">
              <AllFieldsTable row={row} />
            </DetailSection>
          )}

          {activeSection === "metadata" && (
          <DetailSection title="Metadata">
            <SimpleGrid cols={{ base: 1, md: 3 }} spacing="sm">
              <DetailField label="Run ID" value={row.runId} mono />
              <DetailField label="Captured at" value={row.capturedAt} />
              <DetailField label="Content hash" value={row.contentHash} mono />
              <DetailField label="Normalized hash" value={row.normalizedHash} mono />
              <DetailField label="Interface-relevant reasons" value={row.interfaceRelevantReasons} />
              <DetailField label="Affected disciplines" value={row.affectedDisciplines} />
            </SimpleGrid>
          </DetailSection>
          )}
        </Stack>
      </ScrollArea.Autosize>
    </Modal>
  );
};
